import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MessageSquare, Clock, Loader2 } from "lucide-react";
import { useContent } from "../context/SiteContext";
import { useAuth } from "../context/AuthContext";
import { PageHero } from "./PublicLayout";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, formatErr } from "../lib/api";

const TYPES = ["General", "Services", "Existing Order", "Payments", "Become a Grinder", "Other"];

export default function ContactPage() {
  const c = useContent("contact");
  const { user } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "", inquiry_type: "General", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inquiries", f);
      setSent(true);
      toast.success("Message sent");
    } catch (err) {
      toast.error(formatErr(err));
    }
    setBusy(false);
  };
  const cls = "h-12 bg-[#040a1a] border-[#007bff]/35";
  return (
    <div data-testid="contact-page">
      <PageHero eyebrow={c.eyebrow} white={c.title_white} blue={c.title_blue} desc={c.description} />
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-16 grid gap-8 lg:grid-cols-[1fr_1.5fr]">
        <div className="space-y-4">
          {c.email && <div className="card-neon p-5 flex gap-4 items-center"><span className="icon-ring w-12 h-12"><Mail className="w-5 h-5" /></span><div className="min-w-0"><p className="eyebrow !text-[0.7rem]">Email</p><p className="break-all">{c.email}</p></div></div>}
          {c.discord && <div className="card-neon p-5 flex gap-4 items-center"><span className="icon-ring w-12 h-12"><MessageSquare className="w-5 h-5" /></span><div><p className="eyebrow !text-[0.7rem]">Discord</p><p>{c.discord}</p></div></div>}
          {c.hours && <div className="card-neon p-5 flex gap-4 items-center"><span className="icon-ring w-12 h-12"><Clock className="w-5 h-5" /></span><div><p className="eyebrow !text-[0.7rem]">Hours</p><p>{c.hours}</p></div></div>}
          <div className="card-neon p-6">
            <h3 className="font-display text-3xl text-metal">Existing customer?</h3>
            <p className="text-slate-300 mt-2">Message our support team directly from your dashboard for faster help with your order.</p>
            <Link to={user ? "/dashboard/messages" : "/login"} className="btn-ghost mt-4" data-testid="contact-dashboard-support-btn">Contact support in dashboard</Link>
          </div>
        </div>
        {sent ? (
          <div className="card-neon p-10 text-center" data-testid="contact-success">
            <h3 className="font-display text-4xl text-electric">Message received</h3>
            <p className="text-slate-300 mt-2">Thanks for reaching out. We'll get back to you as soon as possible.</p>
            <button className="btn-ghost mt-5" onClick={() => { setSent(false); setF({ ...f, message: "" }); }} data-testid="contact-new-btn">Send another</button>
          </div>
        ) : (
          <form onSubmit={submit} className="card-neon p-6 sm:p-8 grid gap-5 sm:grid-cols-2" data-testid="contact-form">
            <div className="space-y-2"><Label className="font-cond uppercase tracking-wider">Name</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={cls} data-testid="contact-name-input" /></div>
            <div className="space-y-2"><Label className="font-cond uppercase tracking-wider">Email</Label><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={cls} data-testid="contact-email-input" /></div>
            <div className="space-y-2 sm:col-span-2"><Label className="font-cond uppercase tracking-wider">Inquiry type</Label>
              <Select value={f.inquiry_type} onValueChange={(v) => setF({ ...f, inquiry_type: v })}>
                <SelectTrigger className={cls} data-testid="contact-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#060d20]">{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2"><Label className="font-cond uppercase tracking-wider">Message</Label><Textarea required rows={6} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="bg-[#040a1a] border-[#007bff]/35" data-testid="contact-message-input" /></div>
            <button disabled={busy} className="btn-electric sm:col-span-2 h-14" data-testid="contact-submit-btn">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : c.button_text || "Send Message"}</button>
          </form>
        )}
      </div>
    </div>
  );
}
